import useLangText from '@shared/hooks/useLangText';
import { useReceptionSchedules } from '@shared/hooks/useReceptionSchedules';
import type { ReceptionScheduleVm } from '@shared/types/process';

import dayjs from 'dayjs';
import { Clock3 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ReceptionSchedulesSectionProps = {
    serviceId: string;
    receptionSchedules?: ReceptionScheduleVm[];
    onBookingChanged?: () => void;
    hideTimer?: boolean;
};

const formatRemainingTime = (remainingMs: number) => {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [hours, minutes, seconds]
        .map((value) => String(value).padStart(2, '0'))
        .join(':');
};

type ReceptionBookingCountdownProps = {
    expiresAt: string;
    onExpired: () => void;
};

function ReceptionBookingCountdown({
    expiresAt,
    onExpired,
}: ReceptionBookingCountdownProps) {
    const [remainingMs, setRemainingMs] = useState<number | null>(null);
    const expiredFiredRef = useRef(false);
    const onExpiredRef = useRef(onExpired);

    useEffect(() => {
        onExpiredRef.current = onExpired;
    }, [onExpired]);

    useEffect(() => {
        expiredFiredRef.current = false;

        const deadline = new Date(expiresAt).getTime();
        if (Number.isNaN(deadline)) {
            setRemainingMs(null);
            return;
        }

        const update = () => {
            const nextRemainingMs = Math.max(0, deadline - Date.now());
            setRemainingMs(nextRemainingMs);

            if (nextRemainingMs === 0 && !expiredFiredRef.current) {
                expiredFiredRef.current = true;
                onExpiredRef.current();
            }
        };

        update();
        const intervalId = window.setInterval(update, 1000);

        return () => window.clearInterval(intervalId);
    }, [expiresAt]);

    if (remainingMs === null) return null;

    return (
        <time
            aria-live='polite'
            className='flex items-center gap-1.5 text-sm leading-none font-semibold text-(--color-card-text) tabular-nums'
            dateTime={expiresAt}
        >
            <Clock3 aria-hidden='true' className='size-4' />
            {formatRemainingTime(remainingMs)}
        </time>
    );
}

export function ReceptionSchedulesSection({
    serviceId,
    receptionSchedules: providedReceptionSchedules,
    onBookingChanged,
    hideTimer = false,
}: ReceptionSchedulesSectionProps) {
    const { t } = useTranslation();
    const getLangText = useLangText();
    const { schedules: receptionSchedules, refetch } = useReceptionSchedules(
        serviceId,
        !providedReceptionSchedules,
    );

    const sourceSchedules = providedReceptionSchedules ?? receptionSchedules;

    const schedules = useMemo(
        () => sourceSchedules.filter((schedule) => schedule.booking),
        [sourceSchedules],
    );

    const handleBookingExpired = useCallback(() => {
        void refetch().then(() => onBookingChanged?.());
    }, [onBookingChanged, refetch]);

    if (!schedules.length) return null;

    return (
        <section aria-label={t('translation:receptionSchedule')}>
            {schedules.map((schedule) => {
                const booking = schedule.booking!;
                const bookingReleased =
                    booking.status === 'Released' && !booking.expiresAt;

                return (
                    <div key={schedule.buttonId}>
                        {bookingReleased ? (
                            <p
                                className='mt-3 text-sm leading-5 text-(--color-red)'
                                role='status'
                            >
                                {t('translation:bookingStatusExpired')}
                            </p>
                        ) : null}
                        <dl className='mt-6 flex flex-col gap-2.5'>
                            <div className='flex flex-col gap-1'>
                                <dt className='text-xs text-(--color-card-subtitle)'>
                                    {t('translation:organizationName')}
                                </dt>
                                <dd className='text-sm leading-none font-semibold text-(--color-card-text)'>
                                    {typeof schedule.organizationName ===
                                    'string'
                                        ? schedule.organizationName
                                        : schedule.organizationName
                                          ? getLangText(
                                                schedule.organizationName,
                                            )
                                          : schedule.organizationId}
                                </dd>
                            </div>
                            <div className='flex flex-col gap-1'>
                                <dt className='text-xs text-(--color-card-subtitle)'>
                                    {t('translation:date')}
                                </dt>
                                <dd className='text-sm leading-none font-semibold text-(--color-card-text)'>
                                    {dayjs(booking.dateFrom)?.format(
                                        'DD.MM.YYYY',
                                    )}
                                </dd>
                            </div>
                            <div className='flex flex-col gap-1'>
                                <dt className='text-xs text-(--color-card-subtitle)'>
                                    {t('translation:time')}
                                </dt>
                                <dd className='text-sm leading-none font-semibold text-(--color-card-text) tabular-nums'>
                                    {booking.timeFrom.slice(0, 5)} -{' '}
                                    {booking.timeTo.slice(0, 5)}
                                </dd>
                            </div>
                            {booking.expiresAt ? (
                                <div
                                    className={
                                        hideTimer
                                            ? 'hidden'
                                            : 'flex flex-col gap-1'
                                    }
                                >
                                    <dt className='text-xs text-(--color-card-subtitle)'>
                                        {t('translation:forEnding')}
                                    </dt>
                                    <dd>
                                        <ReceptionBookingCountdown
                                            expiresAt={booking.expiresAt}
                                            onExpired={handleBookingExpired}
                                        />
                                    </dd>
                                </div>
                            ) : null}
                        </dl>
                    </div>
                );
            })}
        </section>
    );
}
